import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Send, Siren, Sprout } from 'lucide-react';
import { CHAT_COPY, CHAT_SUGGESTED_QUESTIONS } from '@saesak/shared';
import { AppBar } from '@/components/layout';
import { Button, Chip, Spinner, Textarea } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import type { AiWriterStatus } from '@/types';

interface ChatMessage {
  id: string;
  role: string;
  content: string;
  guardrailTriggered: string | null;
  createdAt: string;
}

interface ChatSession {
  id: string;
  childId: string | null;
  messages?: { content: string }[];
}

/**
 * AI 육아상담 — 명세서 §5.16. 학부모 전용, 1일 10회.
 * 응급 징후가 감지되면 모델 대신 119·응급실 안내가 먼저 나온다(가드레일).
 */
export function ChatPage() {
  const queryClient = useQueryClient();
  const [sessionId, setSessionId] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const status = useQuery({
    queryKey: ['ai', 'chat', 'status'],
    queryFn: () => apiData<AiWriterStatus>('/ai/chat/status'),
  });

  const sessions = useQuery({
    queryKey: ['ai', 'chat', 'sessions'],
    queryFn: () => apiData<ChatSession[]>('/ai/chat/sessions'),
  });

  // 가장 최근 세션을 이어 쓰고, 없으면 첫 질문 때 만든다
  const activeSessionId = sessionId || sessions.data?.[0]?.id || '';

  const thread = useQuery({
    queryKey: ['ai', 'chat', 'thread', activeSessionId],
    queryFn: () =>
      apiData<{ session: { id: string }; messages: ChatMessage[] }>(
        `/ai/chat/sessions/${activeSessionId}`,
      ),
    enabled: Boolean(activeSessionId),
  });

  const send = useMutation({
    mutationFn: async (text: string) => {
      let sid = activeSessionId;
      if (!sid) {
        const session = await apiData<ChatSession>('/ai/chat/sessions', { method: 'POST', body: {} });
        sid = session.id;
        setSessionId(sid);
        // 첫 질문에서 세션을 만들었을 때만 목록을 갱신한다
        void queryClient.invalidateQueries({ queryKey: ['ai', 'chat', 'sessions'] });
      }
      const result = await apiData<{ message: ChatMessage }>(`/ai/chat/sessions/${sid}/messages`, {
        method: 'POST',
        body: { content: text },
      });
      return { sid, ...result };
    },
    onSuccess: ({ sid }) => {
      setContent('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['ai', 'chat', 'thread', sid] });
      void queryClient.invalidateQueries({ queryKey: ['ai', 'chat', 'status'] });
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : '답변을 받지 못했어요.');
      // 실패해도 사용량·이미 저장된 사용자 메시지는 반영한다
      void queryClient.invalidateQueries({ queryKey: ['ai', 'chat', 'thread', activeSessionId] });
      void queryClient.invalidateQueries({ queryKey: ['ai', 'chat', 'status'] });
    },
  });

  const newSession = useMutation({
    mutationFn: () => apiData<ChatSession>('/ai/chat/sessions', { method: 'POST', body: {} }),
    onSuccess: (session) => {
      setSessionId(session.id);
      void queryClient.invalidateQueries({ queryKey: ['ai', 'chat', 'sessions'] });
    },
  });

  const messages = thread.data?.messages ?? [];

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, send.isPending]);

  return (
    <div className="app-shell mx-auto flex w-full max-w-screen-sm flex-col">
      <AppBar
        title="육아상담"
        back
        right={
          <Button size="sm" variant="ghost" icon={<RotateCcw size={14} />} onClick={() => newSession.mutate()}>
            새 대화
          </Button>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-2 p-4">
          <p className="rounded-sm bg-info-soft px-3 py-2 text-sm">{CHAT_COPY.disclaimer}</p>

          {activeSessionId && thread.isPending && <Spinner />}

          {messages.length === 0 && !thread.isPending && (
            <div className="py-4">
              <p className="mb-1 flex items-center gap-1.5 text-lg font-bold">
                <Sprout size={18} className="text-primary-strong" /> {CHAT_COPY.emptyTitle}
              </p>
              <p className="mb-3 text-base text-ink-sub">{CHAT_COPY.emptyBody}</p>
              <div className="flex flex-wrap gap-2">
                {CHAT_SUGGESTED_QUESTIONS.map((question) => (
                  <Chip key={question} tone="neutral" onClick={() => send.mutate(question)}>
                    {question}
                  </Chip>
                ))}
              </div>
            </div>
          )}

          {messages.map((message) => {
            const mine = message.role === 'user';
            return (
              <div key={message.id} className={mine ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className={
                    mine
                      ? 'max-w-[85%] rounded-md rounded-br-sm bg-primary-soft px-3.5 py-2.5'
                      : 'max-w-[85%] rounded-md rounded-bl-sm bg-surface px-3.5 py-2.5 shadow-card'
                  }
                >
                  {message.guardrailTriggered && (
                    <p className="mb-1 flex items-center gap-1 text-sm font-bold text-notice">
                      <Siren size={14} /> 응급 안내
                    </p>
                  )}
                  {mine ? (
                    <p className="whitespace-pre-wrap text-base">{message.content}</p>
                  ) : (
                    <ChatText content={message.content} />
                  )}
                </div>
              </div>
            );
          })}

          {send.isPending && <Spinner label="답변을 준비하는 중이에요" />}
          {error && (
            <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
              {error}
            </p>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <div className="border-t border-line bg-surface p-3 pb-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+12px)]">
        {status.data && (
          <p className="mb-1.5 text-right text-sm text-ink-sub tnum">
            오늘 {status.data.usedToday}/{status.data.dailyLimit}회
          </p>
        )}
        <div className="flex items-end gap-2">
          <Textarea
            rows={1}
            className="max-h-32 flex-1"
            placeholder={CHAT_COPY.placeholder}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <Button
            icon={<Send size={16} />}
            disabled={!content.trim() || send.isPending}
            loading={send.isPending}
            onClick={() => send.mutate(content.trim())}
          >
            보내기
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * 챗봇 답변의 가벼운 마크다운을 렌더링한다 — **굵게**·목록 기호가 원문 그대로 보이던 문제.
 * 프롬프트에서도 마크다운을 쓰지 말라고 지시하지만, 이미 저장된 대화와
 * 모델이 습관적으로 쓰는 표기까지 자연스럽게 보이게 하는 안전망이다.
 */
function ChatText({ content }: { content: string }) {
  return (
    <div className="flex flex-col gap-0.5 text-base">
      {content.split('\n').map((line, index) => {
        if (line.trim() === '') return <span key={index} className="h-1.5" aria-hidden />;

        const bullet = /^\s*[-*•·]\s+(.*)$/.exec(line);
        const heading = /^#{1,4}\s+(.*)$/.exec(line);
        const text = bullet?.[1] ?? heading?.[1] ?? line;

        if (bullet) {
          return (
            <p key={index} className="flex gap-1.5 whitespace-pre-wrap">
              <span aria-hidden>·</span>
              <span>{withBold(text)}</span>
            </p>
          );
        }
        return (
          <p
            key={index}
            // 제목 줄은 위 문단과 살짝 띄운다 — 첫 줄이면 말풍선 여백만 쓴다
            className={`whitespace-pre-wrap ${heading ? 'mt-2 font-bold first:mt-0' : ''}`}
          >
            {withBold(text)}
          </p>
        );
      })}
    </div>
  );
}

/** `**굵게**` 구간만 <b>로 바꾼다 — 그 밖의 별표는 본문 그대로 둔다 */
function withBold(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    nodes.push(<b key={match.index}>{match[1]}</b>);
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}
